import { useMemo, useState } from "react";
import { BellRing, ShieldAlert } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { Switch } from "@/shared/components/Switch";
import { CampoPesquisa } from "@/shared/components/CampoPesquisa";
import { compararPtBr, normalizarBusca } from "@/shared/lib/busca";
import { usePushSubscription } from "@/core/hooks/usePushSubscription";
import { useAuth } from "@/core/hooks/useAuth";
import {
  useDefinirPreferenciaNotificacao,
  useMinhasPreferenciasNotificacao,
} from "@/core/hooks/usePreferenciasNotificacao";

/**
 * Categorias definidas pelo CHECK constraint de `preferencias_notificacao`
 * (migration 0070; "Biblioteca" adicionada na migration 0077, "Escala" na
 * migration 0084, "Comunicados" na migration 0100 e "Visitantes" na migration 0101) — mudar aqui
 * exige mudar o CHECK no banco primeiro.
 * Canal: só "push" existe hoje; e-mail/WhatsApp/SMS futuros aparecem como
 * uma segunda coluna de toggles por categoria quando o canal existir.
 */
const CATEGORIAS = [
  { valor: "Pessoas", rotulo: "Pessoas", descricao: "Cadastros pendentes, convites e mudanças de acesso." },
  { valor: "Modulos", rotulo: "Elos", descricao: "Notificações gerais dos elos ativados na sua igreja." },
  { valor: "Eventos", rotulo: "Eventos", descricao: "Criação, alteração e cancelamento de eventos do seu ministério." },
  { valor: "Reservas", rotulo: "Reservas", descricao: "Aprovação ou recusa de solicitações de reserva." },
  { valor: "Biblioteca", rotulo: "Biblioteca", descricao: "Aprovação, recusa e atraso de empréstimos de livros." },
  { valor: "Escala", rotulo: "Escala", descricao: "Notificações sobre as escalas em que você participa ou que você lidera." },
  { valor: "Comunicados", rotulo: "Comunicados", descricao: "Novos comunicados da sua igreja e avisos de comunicados que serão apagados." },
  { valor: "Visitantes", rotulo: "Visitantes", descricao: "Novos visitantes cadastrados e contatos feitos por líderes de outros ministérios (para quem lidera o elo)." },
  { valor: "Igreja", rotulo: "Igreja", descricao: "Notificações gerais da sua igreja." },
] as const;

/**
 * Ordem de exibição: alfabética (A→Z, pt-BR) pelo nome mostrado ao usuário,
 * independente da ordem em que as categorias foram declaradas acima. Só
 * apresentação — nada disso é persistido nem muda o que o banco/dispatcher
 * enxergam.
 */
/**
 * Categorias de elos só aparecem para quem enxerga o elo: numa igreja onde o elo ainda não foi
 * ativado (ou o banco ainda não o conhece), o interruptor não existe — evita escolher algo que
 * o banco não aceita.
 */
const ELO_DA_CATEGORIA: Record<string, string> = { Comunicados: "comunicados", Visitantes: "visitantes" };

const CATEGORIAS_ORDENADAS = [...CATEGORIAS].sort((a, b) => compararPtBr(a.rotulo, b.rotulo));

/**
 * Tela de autoatendimento para notificações (Frente 2, Fase 3 — Frontend/PWA).
 * Sem link em nenhum menu de navegação principal — acessível pelo ícone de
 * engrenagem no cabeçalho do painel do sino (NotificationsPanel), único
 * ponto de entrada (mesmo padrão de URL protegida usado em /conta/seguranca,
 * só que aqui exposta a partir de um lugar que todo usuário já abre).
 *
 * Semântica opt-out: uma categoria sem preferência salva aparece "ativada"
 * (é o comportamento real do dispatcher — só uma linha explícita com
 * ativo=false bloqueia o envio).
 */
export function NotificacoesConfiguracao() {
  const { estado, processando, ativar, desativar } = usePushSubscription();
  const { data: preferencias, isLoading: carregandoPreferencias } =
    useMinhasPreferenciasNotificacao();
  const definirPreferencia = useDefinirPreferenciaNotificacao();
  const [busca, setBusca] = useState("");
  const { modulosVisiveis } = useAuth();

  const ativoPorCategoria = useMemo(() => {
    const mapa = new Map<string, boolean>();
    preferencias?.forEach((p) => mapa.set(p.categoria, p.ativo));
    return mapa;
  }, [preferencias]);

  // O filtro só decide o que aparece; o estado de cada switch continua vindo de
  // `ativoPorCategoria` (preferências salvas), então pesquisar nunca altera nada.
  const categoriasExibidas = useMemo(() => {
    const termo = normalizarBusca(busca.trim());
    const disponiveis = CATEGORIAS_ORDENADAS.filter((c) => {
      const elo = ELO_DA_CATEGORIA[c.valor];
      return !elo || modulosVisiveis.includes(elo);
    });
    if (!termo) return disponiveis;
    return disponiveis.filter((c) => normalizarBusca(c.rotulo).includes(termo));
  }, [busca, modulosVisiveis]);

  const inscrito = estado.tipo === "pronto" && estado.inscrito;

  return (
    <main className="mx-auto w-full max-w-md px-4 pb-16 pt-10 sm:px-6">
      <header className="mb-6">
        <h1 className="text-xl font-bold text-text">Notificações</h1>
        <p className="mt-1 text-sm text-muted">Controle como você recebe notificações deste app.</p>
      </header>

      <section className="mb-6 rounded-2xl border border-border bg-surface p-5">
        <div className="mb-3 flex items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <BellRing size={18} />
          </span>
          <p className="text-sm font-semibold text-text">Notificações push neste dispositivo</p>
        </div>

        {estado.tipo === "verificando" && <p className="text-sm text-muted">Verificando suporte...</p>}

        {estado.tipo === "nao_suportado" && (
          <p className="flex items-start gap-2 text-sm text-muted">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            {estado.motivo}
          </p>
        )}

        {estado.tipo === "erro" && (
          <p role="alert" className="mb-3 rounded-md bg-danger/10 px-3 py-2 text-xs text-danger">
            {estado.mensagem}
          </p>
        )}

        {(estado.tipo === "pronto" || estado.tipo === "erro") && (
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted">
              {inscrito ? "Ativadas neste dispositivo." : "Desativadas neste dispositivo."}
            </p>
            <Button
              type="button"
              carregando={processando}
              onClick={inscrito ? desativar : ativar}
              className="w-auto px-4"
            >
              {inscrito ? "Desativar" : "Ativar"}
            </Button>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-border bg-surface p-5">
        <p className="mb-4 text-sm font-semibold text-text">Categorias de notificação</p>
        <CampoPesquisa
          valor={busca}
          onChange={setBusca}
          placeholder="Pesquisar Elo..."
          rotulo="Pesquisar Elo"
          className="mb-4"
        />
        {carregandoPreferencias ? (
          <p className="text-sm text-muted">Carregando...</p>
        ) : !categoriasExibidas.length ? (
          <p className="text-sm text-muted">Nenhum elo encontrado para &quot;{busca.trim()}&quot;.</p>
        ) : (
          <ul className="flex flex-col gap-4">
            {categoriasExibidas.map((categoria) => {
              const ativo = ativoPorCategoria.get(categoria.valor) ?? true;
              return (
                <li key={categoria.valor} className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-text">{categoria.rotulo}</p>
                    <p className="text-xs text-muted">{categoria.descricao}</p>
                  </div>
                  <Switch
                    checked={ativo}
                    disabled={definirPreferencia.isPending}
                    label={`Notificações de ${categoria.rotulo}`}
                    onChange={(novoValor) =>
                      definirPreferencia.mutate({ categoria: categoria.valor, ativo: novoValor })
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}
